import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import styles from './ScreenPointsForStaff.module.css';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { formatMoney } from '../../../helpers/format';
import { alignTimezone } from '../../../helpers/dates';

const ScreenPointsForStaff = ({ title, points, categories }) => {
  const { t } = useTranslation();

  const normalizedCategories = categories
    .map((c) => ({
      id: c.id,
      name: c.name,
      minAmount: c.targetPoints ?? 0,
      maxAmount: c.maxPoints ?? null,
      percentage: c.commissionPercentage,
    }))
    .sort((a, b) => a.minAmount - b.minAmount);

  const categoryId = useSelector((state) => state.associates.user.salesCategory.categoryId);
  const qualifyBy = useSelector(
    (state) => state.associates.user.salesCategory.fromCategorizationDate,
  );
  const nextCategorizationDate = useSelector(
    (state) => state.associates.user.salesCategory.nextCategorizationDate,
  );

  const commissionPercentage = useSelector(
    (state) => state.associates.user.salesCategory?.commissionPercentage,
  );

  const sortedCategories = [...normalizedCategories].sort((a, b) => a.minAmount - b.minAmount);

  const currentCategoryIndex = sortedCategories.findIndex((category) => category.id === categoryId);
  const nextCategory = sortedCategories[currentCategoryIndex + 1] || null;

  const progressPercentage = nextCategory
    ? Math.min((points / nextCategory.minAmount) * 100, 100)
    : 100;

  const calculatedCategoryId =
    categoryId ||
    sortedCategories.findLast((c) => points >= c.minAmount)?.id ||
    sortedCategories[0].id;

  const currentLevelDisplay = sortedCategories.findIndex((c) => c.id === calculatedCategoryId) + 1;
  const totalLevels = sortedCategories.length;
  const targetPoints = nextCategory ? nextCategory.minAmount : points;
  const missingPoints = nextCategory ? Math.max(nextCategory.minAmount - points, 0) : 0;

  const qualifyByFormatted = format(alignTimezone(qualifyBy), 'd', { locale: es });
  const qualifyFinishFormatted = nextCategorizationDate
    ? format(alignTimezone(nextCategorizationDate), 'd MMMM yyyy', { locale: es })
    : '';

  const currencyPoints = useSelector(
    (state) => state.associates.user.salesCategory?.currencyPoints,
  );

  return (
    <div>
      <div className='mb-3'>
        <div className='font-large bold'>{title}</div>

        <div className='font-small text-medium-gray mt-1'>
          {t('staffPoints.period')}: {qualifyByFormatted} – {qualifyFinishFormatted}
        </div>
      </div>

      <div className={`${styles.card} mb-2`}>
        <div className={`${styles.cardHeader} mb-2`}>
          <span className='  font-1rem fw-semibold '>{t('staffPoints.yourCurrentCommission')}</span>

          <span className={`${styles.badge} font-smaller semi-bold `}>
            {t('staffPoints.level', {
              current: currentLevelDisplay,
              total: totalLevels,
            })}
          </span>
        </div>

        <div className='mb-2'>
          <span className={`${styles.commissionValue}  bold  text-primary `}>
            {commissionPercentage}%
          </span>
        </div>

        <p className={`${styles.commissionSubtitle} mb-4 font-1rem text-lighter-gray`}>
          {t('staffPoints.commissionSubtitle')}
        </p>

        <div className={styles.statsGrid}>
          <div className={` font-tiny  `}>
            <div className='mb-1 text-medium-gray '>{t('staffPoints.confirmedSales')}</div>
            <div className='  font-normal bold '>
              {formatMoney(points, { decimals: 0, currencySymbol: '$' }, true)} {currencyPoints}
            </div>
          </div>

          <div className={`${styles.alignRight} font-tiny text-right `}>
            <div className='mb-1 text-medium-gray'>{t('staffPoints.updatesEvery24h')}</div>
            <div className='  font-normal bold '>{t('staffPoints.monthlyCut')}</div>
          </div>
        </div>
      </div>

      {nextCategory && (
        <div className={`${styles.card} mb-2`}>
          <div className={`${styles.progressHeader} mb-3`}>
            <span className='  font-1rem fw-semibold '>{t('staffPoints.progressNextTier')}</span>

            <span className='font-small text-medium-gray '>
              {formatMoney(points, { decimals: 0, currencySymbol: '$ ' }, true)} /{' '}
              {formatMoney(targetPoints, { decimals: 0, currencySymbol: '$ ' }, true)}{' '}
              {currencyPoints}
            </span>
          </div>

          <div className={`${styles.progressBarContainer} mb-3`}>
            <div
              className={styles.progressBarFill}
              style={{ width: `${progressPercentage}%` }}
            ></div>
          </div>

          <div className='  font-small text-medium-gray '>
            {t('staffPoints.missingToReach')}{' '}
            <strong>
              {formatMoney(missingPoints, { decimals: 0, currencySymbol: '$ ' }, true)}{' '}
              {currencyPoints}
            </strong>{' '}
            {t('staffPoints.toReachCommission')}{' '}
            <strong>{nextCategory.percentage || nextCategory.commissionPercentage}%</strong>
          </div>
        </div>
      )}

      <div className={`${styles.cardtarget} mb-2`}>
        <h3 className='  font-1rem fw-semibold ml-3 ' style={{ marginBottom: '16px' }}>
          {t('staffPoints.howCommissionIsCalculated')}
        </h3>

        <div className={styles.tiersGrid}>
          {sortedCategories.map((cat, index) => {
            const isActive = cat.id === calculatedCategoryId;

            const isLast = index === sortedCategories.length - 1;
            const isOdd = sortedCategories.length % 2 !== 0;
            const isLastCard = isOdd && index === sortedCategories.length - 1;

            return (
              <div
                key={cat.id || index}
                className={`${styles.tierCard} 
                  ${isActive ? styles.tierActive : ''} 
                  ${isLastCard ? styles.centerLast : ''}`}
                onClick={undefined}
              >
                <div className=' font-extra-small text-lighter-gray'>
                  {isLast
                    ? t('staffPoints.fromLabel')
                    : index === 0
                    ? t('staffPoints.toLabel')
                    : t('staffPoints.betweenLabel')}
                </div>
                <div className={`${styles.tierRange} font-small bold`}>
                  {isLast ? (
                    <>
                      <span className={styles.nowrap}>
                        ${formatMoney(cat.minAmount, { decimals: 0, currencySymbol: '' }, true)}
                      </span>
                    </>
                  ) : index === 0 ? (
                    <>
                      <span className={styles.nowrap}>
                        ${formatMoney(cat.maxAmount, { decimals: 0, currencySymbol: '' }, true)}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className={styles.nowrap}>
                        ${formatMoney(cat.minAmount, { decimals: 0, currencySymbol: '' }, true)}
                      </span>
                      &nbsp;–&nbsp;
                      <span className={styles.nowrap}>
                        ${formatMoney(cat.maxAmount, { decimals: 0, currencySymbol: '' }, true)}
                      </span>
                    </>
                  )}
                </div>

                <div className='font-tiny bold text-primary'>
                  {cat.percentage || cat.commissionPercentage}%
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className={`${styles.disclaimer} font-extra-small`}>
        (*) {t('staffPoints.disclaimer')}
      </div>
    </div>
  );
};

export default ScreenPointsForStaff;
